<script setup lang="ts">
    /**
     * A light in the window's corner where the chat sits, while the agent
     * acts on the page (spec `page-tools-improvements.md`, M12): a small
     * bright light is born in that corner and grows into an aurora that
     * sways and breathes while the agent works, glints at each of its
     * clicks, and draws back into the corner when it lets go. The parent
     * gives it a positioned box as large as the window. Decoration only,
     * hidden from assistive technology: the chat already says what the
     * agent is doing.
     */
    withDefaults(
        defineProps<{
            /** Shown while true, growing in and drawing back out. */
            active: boolean
            /** The corner it grows from: the chat's side. */
            side?: 'left' | 'right'
            /** Each new value is a click of the agent on the page: a glint from the corner. */
            pulse?: number
        }>(),
        { side: 'right', pulse: 0 },
    )
</script>

<template>
    <Transition name="pk-working-aurora">
        <span
            v-if="active"
            class="pk-working-aurora"
            :class="{ 'pk-working-aurora--left': side === 'left' }"
            aria-hidden="true">
            <span class="pk-working-aurora__lights">
                <span class="pk-working-aurora__sway">
                    <span class="pk-working-aurora__light" />
                    <span class="pk-working-aurora__light" />
                    <span class="pk-working-aurora__light" />
                </span>
            </span>
            <span class="pk-working-aurora__core" />
            <span
                v-if="pulse"
                :key="pulse"
                class="pk-working-aurora__core pk-working-aurora__glint" />
        </span>
    </Transition>
</template>

<style lang="scss">
    // Only transforms and opacities move: the lights are radial gradients,
    // soft by themselves, and nothing is blurred or repainted while they
    // drift. `--pk-working-aurora-color` sets the color from outside;
    // without it the aurora takes the agent's color in the widget, the brand
    // elsewhere.
    .pk-working-aurora {
        --pk-working-aurora-source: var(
            --pk-working-aurora-color,
            var(--chatbot-main-color, var(--color-brand))
        );
        --pk-working-aurora-main: var(--pk-working-aurora-source);
        --pk-working-aurora-tone-before: var(--pk-working-aurora-main);
        --pk-working-aurora-tone-after: var(--pk-working-aurora-main);

        // The corner it grows from, and which way its lights drift along
        // the bottom edge
        --pk-working-aurora-x: 100%;
        --pk-working-aurora-flip: 1;

        position: absolute;
        inset: 0;
        pointer-events: none;
        transform-origin: var(--pk-working-aurora-x) 100%;

        // Where the browser can derive them: the main color held to a middle
        // lightness, so a near-black or a pale brand still reads as a light on
        // either theme, and two neighbours of it on the hue wheel. The main
        // color alone otherwise.
        @supports (color: oklch(from red l c h)) {
            --pk-working-aurora-main: oklch(
                from var(--pk-working-aurora-source) clamp(0.55, l, 0.75) c h
            );
            --pk-working-aurora-tone-before: oklch(
                from var(--pk-working-aurora-main) l c calc(h - 50)
            );
            --pk-working-aurora-tone-after: oklch(
                from var(--pk-working-aurora-main) l c calc(h + 50)
            );
        }

        &--left {
            --pk-working-aurora-x: 0%;
            --pk-working-aurora-flip: -1;
        }

        // The aurora, faded out toward the far side of the window, on a
        // floor of the main color so its lights read as one glow and not as
        // spots
        &__lights {
            position: absolute;
            inset: 0;
            overflow: hidden;
            opacity: 0.32;
            mask: radial-gradient(
                ellipse 75% 90% at var(--pk-working-aurora-x) 100%,
                #000 25%,
                transparent 75%
            );

            &::before {
                content: '';
                position: absolute;
                inset: 0;
                background: radial-gradient(
                    ellipse 60% 70% at var(--pk-working-aurora-x) 100%,
                    color-mix(
                        in srgb,
                        var(--pk-working-aurora-main) 35%,
                        transparent
                    ),
                    transparent
                );
            }
        }

        // The lights sway together around the corner, as the curtains of an
        // aurora
        &__sway {
            position: absolute;
            inset: 0;
            transform-origin: var(--pk-working-aurora-x) 100%;
            animation: pk-working-aurora-sway 11s ease-in-out infinite alternate;
        }

        // Three lights, the main color and its two neighbours, each drifting
        // at its own pace. `--pk-working-aurora-offset` is the distance from
        // the chat's side.
        &__light {
            position: absolute;
            right: var(--pk-working-aurora-offset);
            width: 80vmax;
            aspect-ratio: 1;
            border-radius: 50%;
            background: radial-gradient(
                closest-side,
                var(--pk-working-aurora-light),
                transparent
            );
            animation: 7s ease-in-out infinite alternate;

            &:nth-child(1) {
                --pk-working-aurora-light: var(--pk-working-aurora-main);
                --pk-working-aurora-offset: -30vmax;

                bottom: -32vmax;
                animation-name: pk-working-aurora-drift-1;
            }

            &:nth-child(2) {
                --pk-working-aurora-light: var(--pk-working-aurora-tone-after);
                --pk-working-aurora-offset: 15vw;

                bottom: -48vmax;
                animation-name: pk-working-aurora-drift-2;
                animation-duration: 9s;
            }

            &:nth-child(3) {
                --pk-working-aurora-light: var(--pk-working-aurora-tone-before);
                --pk-working-aurora-offset: -48vmax;

                bottom: 10vh;
                animation-name: pk-working-aurora-drift-3;
                animation-duration: 8s;
            }
        }

        &--left &__light {
            right: auto;
            left: var(--pk-working-aurora-offset);
        }

        // The bright core of the light in the corner: it flares while the
        // aurora opens, then breathes while the agent works. Bound to a
        // circle smaller than the window, so its edge never shows while it
        // grows.
        &__core {
            position: absolute;
            inset: 0;
            opacity: 0.08;
            background: radial-gradient(
                circle 75vmin at var(--pk-working-aurora-x) 100%,
                color-mix(in srgb, #fff 45%, var(--pk-working-aurora-main)),
                color-mix(
                        in srgb,
                        var(--pk-working-aurora-main) 70%,
                        transparent
                    )
                    30%,
                color-mix(
                        in srgb,
                        var(--pk-working-aurora-tone-after) 30%,
                        transparent
                    )
                    62%,
                transparent
            );
            animation: pk-working-aurora-breathe 2.6s ease-in-out infinite
                alternate;
        }

        // A glint of the core at each click, on top of its breathing
        &__glint {
            opacity: 0;
            animation: pk-working-aurora-glint 0.9s ease-out;
        }

        // The signal stays, the motion goes: the aurora still, fading in and
        // out
        @media (prefers-reduced-motion: reduce) {
            &__sway,
            &__light,
            &__core {
                animation: none;
            }

            &__glint {
                display: none;
            }
        }
    }

    // Born small and bright in the corner, it opens with a strong ease-out;
    // it draws back into the corner and goes out there
    .pk-working-aurora-enter-active {
        animation: pk-working-aurora-in 1.8s cubic-bezier(0.23, 1, 0.32, 1);

        .pk-working-aurora__core {
            animation: pk-working-aurora-flare 1.8s ease-out;
        }
    }

    .pk-working-aurora-leave-active {
        animation: pk-working-aurora-out 1.4s cubic-bezier(0.77, 0, 0.175, 1)
            forwards;

        .pk-working-aurora__core {
            animation: pk-working-aurora-flare-out 1.4s ease-in forwards;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .pk-working-aurora-enter-active,
        .pk-working-aurora-leave-active {
            animation: none;
            transition: opacity var(--duration-300) ease-out;

            .pk-working-aurora__core {
                animation: none;
            }
        }

        .pk-working-aurora-enter-from,
        .pk-working-aurora-leave-to {
            opacity: 0;
        }
    }

    @keyframes pk-working-aurora-in {
        0% {
            opacity: 0;
            scale: 0.08;
        }

        20% {
            opacity: 1;
        }
    }

    @keyframes pk-working-aurora-out {
        70% {
            opacity: 1;
        }

        100% {
            opacity: 0;
            scale: 0.08;
        }
    }

    @keyframes pk-working-aurora-flare {
        0% {
            opacity: 0;
        }

        25% {
            opacity: 0.6;
        }
    }

    @keyframes pk-working-aurora-flare-out {
        40% {
            opacity: 0;
        }

        80% {
            opacity: 0.4;
        }

        100% {
            opacity: 0;
        }
    }

    @keyframes pk-working-aurora-breathe {
        to {
            opacity: 0.24;
        }
    }

    @keyframes pk-working-aurora-glint {
        30% {
            opacity: 0.35;
        }
    }

    @keyframes pk-working-aurora-sway {
        from {
            rotate: -7deg;
        }

        to {
            rotate: 7deg;
        }
    }

    @keyframes pk-working-aurora-drift-1 {
        to {
            scale: 1.2;
        }
    }

    @keyframes pk-working-aurora-drift-2 {
        to {
            translate: calc(var(--pk-working-aurora-flip) * 18vw) 0;
            scale: 1 1.3;
        }
    }

    @keyframes pk-working-aurora-drift-3 {
        to {
            translate: 0 18vh;
            scale: 1.3 1;
        }
    }
</style>
